import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Over hero (only on home, when not scrolled): transparent + light text
  const overHero = isHome && !scrolled;
  const navBg = overHero
    ? "bg-transparent"
    : "bg-[#F8F5F0]/95 backdrop-blur-md border-b border-[#E5E0D8]";
  const textColor = overHero ? "text-[#F8F5F0]" : "text-[#1A1A1A]";
  const logoColor = overHero ? "text-[#C9A96E]" : "text-[#1A1A1A]";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${navBg}`}
    >
      <div className="container-mps flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
          <span className={`font-serif text-2xl tracking-tight ${logoColor}`}>
            My Private Shopper
          </span>
          <span className={`hidden sm:inline overline ${overHero ? "text-[#C9A96E]" : "text-[#C9A96E]"}`}>
            MPS
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${textColor} hover:text-[#C9A96E] ${
                  isActive ? "text-[#C9A96E]" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/order"
            data-testid="navbar-cta-order"
            className="hidden sm:inline-flex items-center px-5 py-2.5 text-sm uppercase tracking-[0.18em] bg-[#C9A96E] text-[#0A0A0A] hover:bg-[#A68A52] transition-colors"
          >
            Start Your Order
          </Link>
          <button
            data-testid="navbar-mobile-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden p-2 ${textColor}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="navbar-mobile-panel"
          className="lg:hidden bg-[#0A0A0A] text-[#F8F5F0] border-t border-[#C9A96E]/30"
        >
          <div className="container-mps py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base tracking-wide py-2 border-b border-[#C9A96E]/20"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/order"
              data-testid="navbar-mobile-cta-order"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 text-sm uppercase tracking-[0.18em] bg-[#C9A96E] text-[#0A0A0A]"
            >
              Start Your Order
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
